// GDPR-compliant cookie / analytics consent state.
//
// Single source of truth used by:
//   • CookieConsentBanner — to know whether to render the banner.
//   • airbridge.js        — to gate SDK init + event firing behind consent.
//   • Any future analytics integrations.
//
// Storage shape (localStorage key `allure_cookie_consent`):
//   { decided: true, categories: { necessary: true, analytics: bool },
//     decidedAt: '2026-02-...', version: 1 }
//
// Categories:
//   • necessary — auth tokens, session, CSRF, language preference. ALWAYS true.
//   • analytics — Airbridge attribution + user identification.
//
// GDPR expiry: consent is treated as expired after 365 days and the banner
// reappears so users can renew their decision.

const STORAGE_KEY = 'allure_cookie_consent';
const CURRENT_VERSION = 1;
const CONSENT_TTL_DAYS = 365;
const CHANGE_EVENT = 'allure:cookie-consent-changed';

function readRaw() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

function isExpired(decidedAt) {
  if (!decidedAt) return true;
  const ts = new Date(decidedAt).getTime();
  if (!Number.isFinite(ts)) return true;
  return Date.now() - ts > CONSENT_TTL_DAYS * 24 * 60 * 60 * 1000;
}

export function getConsent() {
  const raw = readRaw();
  if (
    !raw
    || raw.version !== CURRENT_VERSION
    || isExpired(raw.decidedAt)
  ) {
    return {
      decided: false,
      categories: { necessary: true, analytics: false },
      decidedAt: null,
    };
  }
  return {
    decided: !!raw.decided,
    categories: {
      necessary: true,
      analytics: !!raw.categories?.analytics,
    },
    decidedAt: raw.decidedAt,
  };
}

export function hasAnalyticsConsent() {
  return getConsent().categories.analytics === true;
}

export function needsConsentDecision() {
  return getConsent().decided === false;
}

export function setConsent(categories) {
  const payload = {
    version: CURRENT_VERSION,
    decided: true,
    categories: {
      necessary: true,
      analytics: !!categories?.analytics,
    },
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* noop */
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: payload }));
  } catch {
    /* noop */
  }
  return payload;
}

export function acceptAll() {
  return setConsent({ analytics: true });
}

export function rejectAll() {
  return setConsent({ analytics: false });
}

// Lets a logged-in user re-open the banner from Privacy settings.
export function reopenConsentBanner() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: null }));
  } catch {
    /* noop */
  }
}

export function onConsentChange(handler) {
  const wrapped = (ev) => handler(ev.detail || null);
  window.addEventListener(CHANGE_EVENT, wrapped);
  return () => window.removeEventListener(CHANGE_EVENT, wrapped);
}

export const COOKIE_CONSENT_CHANGE_EVENT = CHANGE_EVENT;
